// File: src/pages/ContactUs.jsx

import React, { useRef, useEffect, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import FooterLinks from "../LandingPage/FooterLinks";
import SeoMeta from "../SEO/SeoMeta";

export default function ContactUs() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    function updateHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-black">
      <SeoMeta
        title="Contact Us – Million2Trillion"
        description="Reach out to the Million2Trillion team via email, WhatsApp, or phone. We're available globally and regionally to assist with your inquiries."
        keywords="Contact Million2Trillion, M2T support, founder email, Asia regional support, UAE company, fintech contact details"
        url="https://million2trillion.com/contact"
      />

      {/* --- FIXED HEADER --- */}
      <CompanyHeader ref={headerRef} activePage="contact" />
      {/* --- SPACER TO OFFSET HEADER --- */}
      <div style={{ height: headerHeight }} aria-hidden="true" />

      {/* --- BANNER IMAGE (ALWAYS BELOW HEADER) --- */}
      <div className="w-full bg-white">
        <img
          src="/assets/company/Million2Trillion.gif"
          alt="About Million2Trillion"
          className="block w-full"
          style={{
            width: "100vw",
            maxWidth: "100vw",
            height: "auto",
          }}
          draggable={false}
        />
      </div>

      {/* --- PAGE TITLE --- */}
      <main className="mx-auto max-w-4xl px-4 pb-20 pt-12">
        <header className="mb-10 text-center">
          <h1 className="mb-4 text-4xl font-extrabold text-black md:text-5xl">
            Contact Million2Trillion
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-700">
            We welcome your inquiries, collaborations, and support requests.
            <br />
            Choose your preferred way to connect:
          </p>
        </header>

        {/* --- CONTACT CARDS --- */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {/* Email */}
          <section className="rounded-2xl border border-purple-200 bg-gradient-to-br from-white via-gray-50 to-purple-50 p-6 shadow-md">
            <h2 className="mb-2 text-xl font-bold text-purple-700">
              📧 Email Contacts
            </h2>
            <ul className="space-y-2 text-gray-800">
              <li>
                <span className="font-semibold">Founder:</span>{" "}
                <a
                  href="mailto:founder@million2trillion.com"
                  className="text-purple-600 underline"
                >
                  senthil@million2trillion.com
                </a>
              </li>
              <li>
                <span className="font-semibold">Co-Founder:</span>{" "}
                <a
                  href="mailto:satbir@million2trillion.com"
                  className="text-purple-600 underline"
                >
                  satbir@million2trillion.com
                </a>
              </li>
              <li>
                <span className="font-semibold">Support:</span>{" "}
                <a
                  href="mailto:support@million2trillion.com"
                  className="text-purple-600 underline"
                >
                  support@million2trillion.com
                </a>
              </li>
              {/*
              <li><span className="font-semibold">Admin:</span> <a href="mailto:admin@million2trillion.com" className="text-purple-600 underline">admin@million2trillion.com</a></li>
              <li><span className="font-semibold">Sales:</span> <a href="mailto:sales@million2trillion.com" className="text-purple-600 underline">marketing@million2trillion.com</a></li>
              <li><span className="font-semibold">Enquiry:</span> <a href="mailto:enquiry@million2trillion.com" className="text-purple-600 underline">enquiry@million2trillion.com</a></li>
              <li><span className="font-semibold">Legal:</span> <a href="mailto:legal@million2trillion.com" className="text-purple-600 underline">legal@million2trillion.com</a></li>
              */}
            </ul>
          </section>

          {/* WhatsApp */}
          <section className="rounded-2xl border border-green-200 bg-gradient-to-br from-white via-gray-50 to-green-50 p-6 shadow-md">
            <h2 className="mb-2 text-xl font-bold text-green-700">
              📱 WhatsApp Support
            </h2>
            <ul className="space-y-2 text-gray-800">
              <li>
                <span className="font-semibold">Global:</span>{" "}
                <a
                  href="https://wa.me/255789745684"
                  className="text-green-700 underline"
                >
                  +255-789745684
                </a>
              </li>

              <li>
                <span className="font-semibold">Global:</span>{" "}
                <a
                  href="https://wa.me/255756888864"
                  className="text-green-700 underline"
                >
                  +255-756888864
                </a>
              </li>
              <li>
                <span className="font-semibold">Asia Region:</span>{" "}
                <a
                  href="https://wa.me/918148558651"
                  className="text-green-700 underline"
                >
                  +91-8148558651
                </a>
              </li>
            </ul>
          </section>

          {/* Phone */}
          <section className="rounded-2xl border border-blue-200 bg-gradient-to-br from-white via-gray-50 to-blue-50 p-6 shadow-md">
            <h2 className="mb-2 text-xl font-bold text-blue-700">📞 Call Us</h2>
            <p className="mb-1 text-gray-800">
              <span className="font-semibold">Global:</span> +255-789745684
            </p>
            <p className="mb-1 text-gray-800">
              <span className="font-semibold">Global:</span> +255-756888864
            </p>
            <p className="text-sm text-gray-500">
              Additional global numbers coming soon
            </p>
          </section>

          {/* Company Info */}
          <section className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-gray-50 to-gray-100 p-6 shadow-md">
            <h2 className="mb-2 text-xl font-bold text-gray-800">
              🏢 Company Details
            </h2>
            <p className="text-gray-800">
              <span className="font-semibold">
                Million2Trillion operates under:
              </span>{" "}
              <br />
              <span>Million2Trillion Inc. (United States)</span>
            </p>
            <p className="mt-2 text-gray-800">
              <span className="font-semibold">Head Office (USA):</span> <br />
              United States (serving US & Europe region)
            </p>
            <p className="mt-2 text-gray-800">
              <span className="font-semibold">
                Middle East & Africa Office:
              </span>{" "}
              <br />
              United Arab Emirates (UAE)
            </p>
            <p className="mt-2 text-gray-800">
              <span className="font-semibold">Asia Regional Office:</span>{" "}
              <br />
              India
            </p>
          </section>
        </div>

        {/* CTA */}
        <section className="mt-16 text-center">
          <h3 className="mb-2 text-2xl font-bold text-purple-500">
            We're here to help you build, connect, and grow in a trusted global
            financial market ecosystem.
          </h3>
          <p className="text-gray-700">
            Reach out, and our team will respond within 24 hours.
          </p>
        </section>
      </main>

      {/* --- FOOTER --- */}
      <footer className="mt-36 border-t border-gray-800 bg-black">
        <FooterLinks />
      </footer>
    </div>
  );
}
